import Database from "./Database";
import { firestore } from "firebase-admin";
import { error, info, warn } from "../../logger";
import Cache from "../cache/Cache";
import GetCache from "../cache";
import crypto from "crypto";

export class FirestoreDatabase implements Database {
  private _db: firestore.Firestore;
  private _cache: Cache;

  constructor(firestore: firestore.Firestore) {
    this._cache = GetCache();
    this._db = firestore;
  }

  async Save(collectionName: string, data: any): Promise<string> {
    const operation: string = "SaveDocument";
    try {
      if (data.id) {
        await this._db
          .collection(collectionName)
          .doc(data.id)
          .set({ ...data, modified: Date.now() });
        info(
          `The id was provided. Setting document [${data.id}] in collection ${collectionName} instead create a new one.`,
          { operation, collectionName, documentId: data.id }
        );
        await this._cache.delPattern(`${collectionName}::*`); //Clear cache by pattern
        return data.id;
      }

      const docRef = await this._db
        .collection(collectionName)
        .add({ ...data, modified: Date.now() });
      info(
        `Creating a new document with id ${docRef.id} in collection ${collectionName}.`,
        { operation, collectionName, documentId: data.id }
      );
      await this._cache.delPattern(`${collectionName}::*`); //Clear cache by pattern
      return docRef.id;
    } catch (err: any) {
      error(`Could not Save Data in ${collectionName} - ${err.message}`, {
        collectionName,
        data,
        operation,
      });
      throw new Error("Could not save item in Database");
    }
  }

  async FindOne(collectionName: string, id: string): Promise<any> {
    const operation: string = "FindOne";
    try {
      const cacheKey = this.__getCacheKey(collectionName, id);
      const cacheData = await this._cache.get(cacheKey);

      if (cacheData) return cacheData;

      const docRef = this._db.collection(collectionName).doc(id);
      const docSnapshot = await docRef.get();

      if (docSnapshot.exists) {
        info(
          `Fetching data with sucess in collection ${collectionName} with id ${id}`,
          { collectionName, documentId: id, operation }
        );
        const data = docSnapshot.data();
        this._cache.set(cacheKey, data);
        return data;
      }

      warn(
        `Don't exists any document in collection ${collectionName} with id ${id}`,
        { collectionName, documentId: id, operation }
      );
      return null;
    } catch (err: any) {
      error(
        `Could not FindOne Data - ${collectionName}[${id}] - ${err.message}`,
        { collectionName, documentId: id, operation }
      );

      throw new Error("Could not fetch data. Try again later");
    }
  }

  async QueryData(
    collectionName: string,
    wheres: {
      field: string;
      operator: FirebaseFirestore.WhereFilterOp;
      value: any;
    }[],
    orderBy: { field: string; direction: firestore.OrderByDirection },
    noCache?: boolean
  ): Promise<Array<any>> {
    const operation: string = "QueryData";

    try {
      let queryRef: FirebaseFirestore.Query =
        this._db.collection(collectionName);

      wheres?.forEach((where) => {
        queryRef = queryRef.where(where?.field, where?.operator, where?.value);
      });

      if (orderBy) {
        queryRef = queryRef.orderBy(orderBy.field, orderBy.direction);
      }

      const cacheKey = this.__getCacheKey(collectionName, queryRef);

      const cached = await this._cache.get(cacheKey);
      if (cached && !noCache) return cached;

      const querySnapshot = await queryRef.get();

      const res = querySnapshot.docs.map((doc) => doc.data());

      if (!noCache) this._cache.set(cacheKey, res);

      return res;
    } catch (err: any) {
      error(
        `Could not Query Data - ${collectionName}[Wheres: ${JSON.stringify(
          wheres
        )}][OrderBy >> Field: ${orderBy?.field} | Direction: ${
          orderBy?.direction
        }] - ${err.message}`,
        { collectionName, operation }
      );
      throw new Error(err.message);
    }
  }

  async Delete(
    collectionName: string,
    query: { field: string; value: string }
  ): Promise<any> {
    const operation: string = "DeleteData";
    try {
      const idsDeleted: string[] = [];
      const queryResult = this._db
        .collection(collectionName)
        .where(query.field, "==", query.value);
      const snapshot = await queryResult.get();
      const batch = this._db.batch();

      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
        idsDeleted.push(doc.id);
      });

      await batch.commit();
      info(
        `Deleting data in collection ${collectionName}. Ids deleted: ${idsDeleted}`,
        { collectionName, operation }
      );

      await this._cache.delPattern(`${collectionName}::*`);
    } catch (err: any) {
      error(
        `Could not Delete Data - ${collectionName}[Wheres: ${JSON.stringify(
          query
        )}] - ${err.message}`,
        { collectionName, operation }
      );
      throw new Error("Error while delete. Try again later");
    }
  }

  async Update(collectionName: string, id: string, data: any): Promise<any> {
    const operation: string = "UpdateDocument";
    try {
      const docRef = this._db.collection(collectionName).doc(id);
      await docRef.update({ ...data, modified: Date.now() });
      info(`Updating document ${id} in collection ${collectionName}`, {
        operation,
        documentId: id,
        collectionName,
      });
      await this._cache.delPattern(`${collectionName}::*`); //Clear cache by pattern
      return true;
    } catch (err: any) {
      error(
        `Could not Update Document - ${collectionName}[${id}] - ${err.message}`,
        { collectionName, documentId: id, data, operation }
      );
      throw new Error("Error while Update data. Try again later.");
    }
  }

  private __getCacheKey = (collectionName: string, data: any) => {
    const hashStr = crypto
      .createHash("md5")
      .update(JSON.stringify(data))
      .digest("hex");
    return `${collectionName}::${hashStr}`;
  };
}
